import { MouseEvent, ReactNode } from 'react';

import { Credential } from 'admin/credentials/Credentials';
import { ModuleGroup } from 'admin/groups/Groups';
import { Profile } from 'auth/Auth';
import { ResponseEditorWidgetPlacement } from 'common/response-editor/ResponseEditor';
import {
    RespondButtonOptions,
    ResponseGeneratorOptions,
    ToolBarOptions,
} from 'common/response-editor/tool-bar/ToolBar';
import { TemplateLevel } from 'common/templates/Templates';
import { ButtonVariant } from 'core/util/Bootstrap';
import { Config } from 'core/util/Config';
import { LocaleCode } from 'core/util/Locale';
import { SortOrder } from 'core/util/Search';
import { Ticket } from 'tickets/actions2/common/action.types';

export interface ReviewProps {
    review?: any;
    reviewId?: string;
    conversationId?: string;
    profile?: Profile;
    replies?: any[];
    handlers?: Handlers;
    options?: Options;
    config?: Config;
    className?: string;
    conversation?: any;
    actions?: Record<string, boolean>;
    rid?: string;
    mode?: ReviewMode;
    rrid?: string;
    actionMRRenderer?: ({
        isFlagged,
        isPublished,
        flagCallback,
        publishCallback,
    }: {
        isFlagged: boolean;
        isPublished: boolean;
        flagCallback: () => Promise<void>;
        publishCallback: () => Promise<void>;
    }) => ReactNode;
    reviewTicket?: Ticket | undefined;
    isNorthStarEnabled?: boolean;
}

export enum ReviewMode {
    Respond = 1,
    Approve = 2,
    Edit = 3,
}

export interface ContextProps {
    context: ReviewProps;
}

export enum Label {
    Read = 1,
    Profanity = 2,
    Published = 3,
    Syndicated = 5,
    Unsyndicated = -5,
    Responded = 6,
    Flagged = 11,
    Pending = 22,
    NotRespondable = -21,
    Updated = 26,
    NeedsResponse = 32,
    NoResponseRequired = 33,
}

export enum ReplyTo {
    Review = 1,
    Reply = 2,
}

export enum ReplyMode {
    Add = 1,
    Edit = 2,
    Recommend = 3,
}

export enum ReadOnlyReason {
    Auth = 1,
    Source = 2,
    Credentials = 3,
    NotRespondable = 4,
    Reports = 5,
    View = 6,
    ConversationClosed = 7,
}

export interface Handlers {
    reviewClick?: (review: any, event?: MouseEvent<HTMLElement>) => void;
    reviewHistoryClick?: (show: boolean) => void;
    forwardReview?: (review: any) => void;
    provideFeedback?: (review: any) => void;
    redirectReview?: (feed: string, legacyId: string) => void;
    createTicket?: (review?: any) => void;
    updateConversation?: (conversation: any) => void;
    onSaveCredential?: (review: any, credential: Credential) => void;
    onFailedSaveCredential?: (error: any) => void;
    onSuccessfulResponse?: (result: any) => void;
}

export interface Options {
    readOnly?: boolean;
    locale?: LocaleCode;
    showEditor?: boolean;
    showSource?: boolean;
    showActions?: boolean;
    showReplies?: boolean;
    showLocation?: boolean;
    showReviewerName?: boolean;
    labelOptions?: LabelOptions;
    editorOptions?: EditorOptions;
    sourceOptions?: SourceOptions;
    readOnlyReason?: ReadOnlyReason;
    repliesOptions?: RepliesOptions;
    propertyOptions?: PropertyOptions;
    categoryOptions?: CategoryOptions;
    subRatingOptions?: SubRatingOptions;
    historyOptions?: HistoryOptions;
    reviewerOptions?: ReviewerOptions;
    actionOptions?: ActionOptions;
    renderReplyOptions?: (moderationHandler: (approved: boolean) => Promise<void>) => ReactNode;
}

interface SourceOptions {
    logo?: string;
}

interface LabelOptions {
    collapse?: boolean;
}

interface CategoryOptions {
    showLabel?: boolean;
}

interface EditorOptions {
    buttonText?: string;
    placeholder?: string;
    readOnlyText?: string;
    enableMedia?: boolean;
    enableEmojis?: boolean;
    showCharCount?: boolean;
    enableTemplates?: boolean;
    moduleGroups?: ModuleGroup[];
    templateLevels?: TemplateLevel[];
    widgetPlacement?: ResponseEditorWidgetPlacement;
    responseGeneratorOptions?: ResponseGeneratorOptions;
    enableCancel?: boolean;
    cancelText?: string;
    defaultText?: string;
    respondButtonOptions?: RespondButtonOptions;
    allowAddCredentials?: boolean;
    actionsRespondButtonHandler?: (responseOption: any, responseResult: any) => void;
    filterTemplates?: boolean;
    enableVOBResponding?: boolean;
    generateAIResponseOnLoad?: boolean;
    toolBarOptions?: ToolBarOptions;
}

interface SubRatingOptions {
    showLabel?: boolean;
}

interface PropertyOptions {
    showLabel?: boolean;
}

interface RepliesOptions {
    count?: number;
    showDelete?: boolean;
    expand?: boolean;
    showToggle?: boolean;
    showCustomActions?: boolean;
    customActions?: ReplyAction[];
    sortOrder?: SortOrder;
    showModeration?: boolean;
    canAdd?: boolean;
}

interface ReplyAction {
    key: string;
    label: string;
    icon: string;
    disabled?: boolean;
    variant: ButtonVariant;
    showFor?: string | string[];
    onClick: (type: any, reply: any) => void;
}

interface HistoryOptions {
    showHistory?: boolean;
}

interface ReviewerOptions {
    replaceNameWith?: string;
}

interface ActionOptions {
    hideFlag?: boolean;
    hideForward?: boolean;
    hideShare?: boolean;
    hideMarkAsRead?: boolean;
    hideFeedback?: boolean;
    hidePublish?: boolean;
    hideCreateTicket?: boolean;
    hideRemoveNeedsResponse?: boolean;
}
